import { useState, useEffect } from 'react';
import { useLanguage } from '../context/LanguageContext';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

// Cache côté client avec localStorage (persistant)
const clientCache = {
  get: (key) => {
    try {
      const item = localStorage.getItem(`translate_${key}`);
      if (!item) return null;
      
      const { value, timestamp } = JSON.parse(item);
      
      // Expirer après 7 jours (au lieu de 24h pour réduire les requêtes)
      if (Date.now() - timestamp > 7 * 24 * 60 * 60 * 1000) {
        localStorage.removeItem(`translate_${key}`);
        return null;
      }
      
      return value;
    } catch (err) {
      console.error('Erreur lecture cache localStorage:', err);
      return null;
    }
  },
  
  set: (key, value) => {
    try {
      localStorage.setItem(`translate_${key}`, JSON.stringify({
        value,
        timestamp: Date.now()
      }));
    } catch (err) {
      console.error('Erreur écriture cache localStorage:', err);
    }
  },
  
  has: (key) => {
    return clientCache.get(key) !== null;
  },
  
  clear: () => {
    try {
      // Supprimer uniquement les clés de traduction
      Object.keys(localStorage)
        .filter(key => key.startsWith('translate_'))
        .forEach(key => localStorage.removeItem(key));
    } catch (err) {
      console.error('Erreur vidage cache localStorage:', err);
    }
  }
};

// État global pour gérer la queue de traduction
let translationsInProgress = 0;
let realTranslationsInProgress = 0; // Traductions réellement générées (pas du cache)
let translationQueue = [];
const MAX_CONCURRENT_TRANSLATIONS = 10; // Augmenté car le cache est rapide

// Fonction pour traiter la queue
const processTranslationQueue = async () => {
  if (translationsInProgress >= MAX_CONCURRENT_TRANSLATIONS || translationQueue.length === 0) {
    return;
  }

  const nextTranslation = translationQueue.shift();
  if (nextTranslation) {
    translationsInProgress++;
    try {
      await nextTranslation();
    } finally {
      translationsInProgress--;
      processTranslationQueue(); // Traiter le prochain
    }
  }
};

export const useTranslate = (frenchText) => {
  const { currentLanguage } = useLanguage();
  const [translatedText, setTranslatedText] = useState(frenchText); // COMMENCE AVEC LE FRANÇAIS
  const [isTranslating, setIsTranslating] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    // Si la langue est le français, pas besoin de traduire
    if (currentLanguage === 'fr' || !frenchText) {
      setTranslatedText(frenchText);
      return;
    }

    // Clé de cache unique
    const cacheKey = `${frenchText}__${currentLanguage}`;

    // Vérifier le cache client (localStorage)
    if (clientCache.has(cacheKey)) {
      setTranslatedText(clientCache.get(cacheKey));
      return;
    }

    // Ajouter à la queue de traduction
    const translate = async () => {
      setIsTranslating(true);
      setError(null);

      try {
        const response = await fetch(`${API_BASE}/api/translate`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            text: frenchText,
            target_language: currentLanguage
          }),
          signal: AbortSignal.timeout(15000) // Timeout 15s
        });

        if (!response.ok) {
          throw new Error(`Erreur traduction: ${response.status}`);
        }

        const data = await response.json();
        const translated = data.translated_text;
        const fromCache = data.from_cache;

        // Si PAS du cache serveur, incrémenter le compteur de vraies traductions
        if (!fromCache) {
          realTranslationsInProgress++;
          // Décrémenter après un court délai
          setTimeout(() => {
            realTranslationsInProgress = Math.max(0, realTranslationsInProgress - 1);
          }, 100);
        }

        // Sauvegarder dans le cache client (localStorage)
        clientCache.set(cacheKey, translated);
        setTranslatedText(translated);
      } catch (err) {
        console.error('Erreur de traduction:', err);
        setError(err.message);
        // En cas d'erreur, garder le texte français
        setTranslatedText(frenchText);
      } finally {
        setIsTranslating(false);
      }
    };

    // Ajouter à la queue au lieu d'exécuter directement
    translationQueue.push(translate);
    processTranslationQueue();
  }, [frenchText, currentLanguage]);

  return { translatedText, isTranslating, error };
};

// Hook pour savoir si des traductions sont en cours globalement
// Ne retourne true que s'il y a des VRAIES traductions (pas du cache)
export const useTranslationStatus = () => {
  const [isTranslating, setIsTranslating] = useState(false);
  const { currentLanguage } = useLanguage();

  useEffect(() => {
    if (currentLanguage === 'fr') {
      setIsTranslating(false);
      return;
    }

    // Vérifier périodiquement si des traductions RÉELLES sont en cours
    const checkStatus = () => {
      // Afficher le bandeau seulement s'il y a des vraies traductions en cours
      // OU si la queue est très longue (plus de 20 éléments)
      const showBanner = realTranslationsInProgress > 0 || translationQueue.length > 20;
      setIsTranslating(showBanner);
    };

    checkStatus();
    const interval = setInterval(checkStatus, 300);

    return () => {
      clearInterval(interval);
    };
  }, [currentLanguage]);

  return isTranslating;
};

// Hook pour traduire un tableau de textes
export const useTranslateMultiple = (frenchTexts) => {
  const { currentLanguage } = useLanguage();
  const [translatedTexts, setTranslatedTexts] = useState(frenchTexts);
  const [isTranslating, setIsTranslating] = useState(false);

  useEffect(() => {
    if (currentLanguage === 'fr' || !frenchTexts || frenchTexts.length === 0) {
      setTranslatedTexts(frenchTexts);
      return;
    }

    const translateAll = async () => {
      setIsTranslating(true);

      try {
        const translations = await Promise.all(
          frenchTexts.map(async (text) => {
            const cacheKey = `${text}__${currentLanguage}`;
            
            // Vérifier cache localStorage
            if (clientCache.has(cacheKey)) {
              return clientCache.get(cacheKey);
            }

            const response = await fetch(`${API_BASE}/api/translate`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json'
              },
              body: JSON.stringify({
                text,
                target_language: currentLanguage
              })
            });

            if (!response.ok) {
              return text; // Fallback au texte original
            }

            const data = await response.json();
            const translated = data.translated_text;
            clientCache.set(cacheKey, translated);
            return translated;
          })
        );

        setTranslatedTexts(translations);
      } catch (err) {
        console.error('Erreur de traduction multiple:', err);
        setTranslatedTexts(frenchTexts);
      } finally {
        setIsTranslating(false);
      }
    };

    translateAll();
  }, [frenchTexts, currentLanguage]);

  return { translatedTexts, isTranslating };
};

// Composant utilitaire pour afficher du texte traduit
export const T = ({ children }) => {
  const { translatedText } = useTranslate(children);
  return <>{translatedText}</>;
};

// Fonction utilitaire pour vider le cache client (accessible depuis l'admin)
export const clearClientTranslationCache = () => {
  clientCache.clear();
  console.log('✅ Cache client de traduction vidé');
};
